import { UseFormReturn } from 'react-hook-form';
import { useLanguage } from '@/hooks/useLanguage';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreditCard, DollarSign, CheckCircle } from 'lucide-react';
import { useState } from 'react';

interface PaymentStepProps {
  form: UseFormReturn<any>;
  onSelect?: () => void;
}

const PaymentStep = ({ form, onSelect }: PaymentStepProps) => {
  const { language } = useLanguage();
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const paymentMethod = form.watch('paymentMethod');

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.size <= 20 * 1024 * 1024) {
      setUploadedFile(file);
      form.setValue('paymentEvidenceFile', file);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold mb-2">
          {language === 'es' ? 'Método de Pago' : 'Payment Method'}
        </h3>
        <p className="text-muted-foreground">
          {language === 'es' 
            ? 'Tarifa de consulta: $150 USD (no reembolsable)'
            : 'Consultation Fee: $150 USD (non-refundable)'}
        </p>
      </div>

      <div className="bg-destructive/20 border-2 border-destructive rounded-lg p-6" role="alert" aria-live="polite">
        <div className="flex items-start gap-3">
          <DollarSign className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-destructive text-lg mb-2">
              {language === 'es' ? '⚠️ Política de Pago' : '⚠️ Payment Policy'}
            </p>
            <p className="text-destructive font-semibold">
              {language === 'es'
                ? 'La tarifa de consulta de $150 no es reembolsable. Este pago asegura su cita.'
                : 'The $150 consultation fee is non-refundable. This payment secures your appointment.'}
            </p>
          </div>
        </div>
      </div>

      <FormField
        control={form.control}
        name="paymentMethod"
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              {language === 'es' ? 'Seleccione Método de Pago' : 'Select Payment Method'} *
            </FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="grid grid-cols-1 gap-4"
              >
                {/* Stripe - Credit/Debit Card */}
                <Card 
                  className={`cursor-pointer transition-all ${
                    field.value === 'Stripe' ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => { field.onChange('Stripe'); onSelect?.(); }}
                >
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="Stripe" id="stripe" />
                      <CreditCard className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">
                        {language === 'es' ? 'Tarjeta de Crédito/Débito' : 'Credit/Debit Card'}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      {language === 'es'
                        ? 'Pago seguro con tarjeta. Procesado inmediatamente por Stripe.'
                        : 'Secure card payment. Processed immediately via Stripe.'}
                    </CardDescription>
                  </CardContent>
                </Card>

                {/* Check/Wire */}
                <Card 
                  className={`cursor-pointer transition-all ${
                    field.value === 'Check' || field.value === 'Wire' ? 'border-primary bg-primary/5' : ''
                  }`}
                  onClick={() => { field.onChange('Check'); onSelect?.(); }}
                >
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="Check" id="check" />
                      <CheckCircle className="h-5 w-5 text-primary" />
                      <CardTitle className="text-base">
                        {language === 'es' ? 'Cheque / Transferencia Bancaria' : 'Check / Wire Transfer'}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>
                      {language === 'es'
                        ? 'Pago mediante cheque o transferencia bancaria directa.'
                        : 'Payment via check or direct wire transfer.'}
                    </CardDescription>
                  </CardContent>
                </Card>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Payment Instructions */}
      {paymentMethod === 'Stripe' && (
        <Alert>
          <CreditCard className="h-4 w-4" />
          <AlertDescription>
            <p className="text-sm">
              {language === 'es'
                ? 'Será redirigido a una página segura de Stripe para completar su pago después de enviar este formulario.'
                : 'You will be redirected to a secure Stripe payment page after submitting this form.'}
            </p>
          </AlertDescription>
        </Alert>
      )}

      {(paymentMethod === 'Check' || paymentMethod === 'Wire') && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {language === 'es' ? 'Instrucciones de Pago' : 'Payment Instructions'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="font-semibold text-sm mb-2">
                {language === 'es' ? 'Cheque:' : 'Check:'}
              </p>
              <p className="text-sm">
                {language === 'es' ? 'Hacer cheque a nombre de:' : 'Make check payable to:'}<br />
                <span className="font-semibold">Daniel Albert Law Firm</span>
              </p>
              <p className="text-sm mt-2">
                {language === 'es' ? 'Enviar a:' : 'Mail to:'}<br />
                10925 Beechnut St A106<br />
                Houston, TX 77072
              </p>
            </div>

            <div className="border-t pt-3">
              <p className="font-semibold text-sm mb-2">
                {language === 'es' ? 'Transferencia Bancaria:' : 'Wire Transfer:'}
              </p>
              <p className="text-sm">
                {language === 'es'
                  ? 'Por favor contáctenos para obtener la información bancaria:'
                  : 'Please contact us for wire transfer details:'}
                <br />
                <a href="tel:8329303059" className="text-primary hover:underline">
                  (832) 930-3059
                </a>
                {' | '}
                <a href="mailto:info@danielalbertlaw.com" className="text-primary hover:underline">
                  info@danielalbertlaw.com
                </a>
              </p>
            </div>

            {/* Upload Receipt */}
            <div className="space-y-2 pt-3 border-t">
              <FormLabel>
                {language === 'es' ? 'Subir Comprobante (Opcional)' : 'Upload Receipt (Optional)'}
              </FormLabel>
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileUpload}
                className="cursor-pointer"
              />
              {uploadedFile && (
                <p className="text-sm text-muted-foreground">
                  ✓ {uploadedFile.name}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PaymentStep;
